import { useEffect, useState, useCallback } from "react";
import { Loader2, LogOut, Inbox, Building2, Mail, Users, ShieldAlert, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUSES = ["New", "Contacted", "In Progress", "Completed", "Archived"];
const statusColor = {
  New: "bg-sky/15 text-ocean",
  Contacted: "bg-amber-100 text-amber-700",
  "In Progress": "bg-violet-100 text-violet-700",
  Completed: "bg-emerald-100 text-emerald-700",
  Archived: "bg-slate-100 text-slate-500",
};

const TABS = [
  { key: "assessment", label: "Assessments", icon: Inbox },
  { key: "listing", label: "Property Submissions", icon: Building2 },
  { key: "contact", label: "Contact Inquiries", icon: Mail },
];

function LoginScreen() {
  const handleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <div className="flex min-h-screen items-center justify-center bg-navy px-5" data-testid="admin-login">
      <div className="w-full max-w-md rounded-3xl border border-white/10 bg-white/5 p-10 text-center backdrop-blur-xl">
        <div className="flex justify-center"><Logo variant="light" /></div>
        <h1 className="mt-8 font-serif text-3xl text-white">Admin Access</h1>
        <p className="mt-3 text-sm text-white/60">Sign in with your authorized Google account to manage leads.</p>
        <Button onClick={handleLogin} data-testid="admin-google-login" className="mt-8 w-full rounded-full bg-white py-6 text-base font-semibold text-navy hover:bg-white/90">
          <svg className="h-5 w-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
          Sign in with Google
        </Button>
      </div>
    </div>
  );
}

function Unauthorized({ onLogout }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-navy px-5" data-testid="admin-unauthorized">
      <div className="w-full max-w-md rounded-3xl border border-white/10 bg-white/5 p-10 text-center backdrop-blur-xl">
        <ShieldAlert className="mx-auto h-12 w-12 text-sky" />
        <h1 className="mt-6 font-serif text-2xl text-white">Not Authorized</h1>
        <p className="mt-3 text-sm text-white/60">This account is not authorized to access the admin dashboard.</p>
        <Button onClick={onLogout} className="mt-8 rounded-full bg-white px-8 text-navy hover:bg-white/90">Sign out</Button>
      </div>
    </div>
  );
}

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="rounded-2xl border border-line bg-white p-6 shadow-float" data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
    <div className="flex items-center justify-between">
      <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-navy text-sky"><Icon className="h-5 w-5" /></span>
      <span className="font-serif text-3xl font-bold text-navy">{value}</span>
    </div>
    <p className="mt-4 text-sm font-medium text-slate-500">{label}</p>
  </div>
);

function Dashboard() {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState({ assessments: 0, submissions: 0, inquiries: 0, total: 0 });
  const [tab, setTab] = useState("assessment");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null);

  const loadStats = useCallback(async () => {
    try { const { data } = await api.get("/admin/stats"); setStats(data); } catch { /* */ }
  }, []);

  const loadLeads = useCallback(async (t) => {
    setLoading(true);
    try { const { data } = await api.get(`/admin/leads/${t}`); setLeads(data); }
    catch { setLeads([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { loadLeads(tab); }, [tab, loadLeads]);

  const updateStatus = async (id, status) => {
    setLeads((cur) => cur.map((l) => (l.id === id ? { ...l, status } : l)));
    try { await api.patch(`/admin/leads/${tab}/${id}/status`, { status }); loadStats(); }
    catch { loadLeads(tab); }
  };

  const fmtDate = (iso) => { try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); } catch { return "-"; } };

  return (
    <div className="min-h-screen bg-mist" data-testid="admin-dashboard">
      <header className="sticky top-0 z-20 border-b border-line bg-white/90 backdrop-blur-xl">
        <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 sm:px-8">
          <Logo />
          <div className="flex items-center gap-4">
            <span className="hidden text-sm text-slate-500 sm:inline">{user?.email}</span>
            <Button variant="outline" onClick={logout} className="rounded-full border-line" data-testid="admin-logout"><LogOut className="h-4 w-4" /> Sign out</Button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 py-10 sm:px-8">
        <h1 className="font-serif text-3xl font-bold text-navy">Leads Dashboard</h1>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard icon=  {Inbox} label="New Assessments" value={stats.assessments} />
          <StatCard icon={Building2} label="Property Submissions" value={stats.submissions} />
          <StatCard icon={Mail} label="Contact Inquiries" value={stats.inquiries} />
          <StatCard icon={Users} label="Total Leads" value={stats.total} />
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-2">
          {TABS.map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`admin-tab-${t.key}`}
              className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-medium transition-colors ${tab === t.key ? "bg-navy text-white" : "border border-line bg-white text-navy/70 hover:text-navy"}`}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
          <Button variant="ghost" onClick={() => { loadLeads(tab); loadStats(); }} className="ml-auto rounded-full text-slate-500" data-testid="admin-refresh"><RefreshCw className="h-4 w-4" /> Refresh</Button>
        </div>

        <div className="mt-6 overflow-hidden rounded-2xl border border-line bg-white shadow-float">
          {loading ? (
            <div className="flex items-center justify-center py-20 text-slate-400"><Loader2 className="h-6 w-6 animate-spin" /></div>
          ) : leads.length === 0 ? (
            <div className="py-20 text-center text-slate-400" data-testid="admin-empty">No leads yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[720px] text-left text-sm">
                <thead className="border-b border-line bg-mist/60 text-xs uppercase tracking-wider text-slate-500">
                  <tr>
                    <th className="px-5 py-4">Name</th>
                    <th className="px-5 py-4">Property</th>
                    <th className="px-5 py-4">City</th>
                    <th className="px-5 py-4">Contact</th>
                    <th className="px-5 py-4">Date</th>
                    <th className="px-5 py-4">Status</th>
                    <th className="px-5 py-4"></th>
                  </tr>
                </thead>
                <tbody>
                  {leads.map((l) => (
                    <tr key={l.id} className="border-b border-line last:border-0 hover:bg-mist/40" data-testid={`admin-row-${l.id}`}>
                      <td className="px-5 py-4 font-medium text-navy">{l.name}</td>
                      <td className="px-5 py-4 text-slate-600">{l.property_name || "-"}</td>
                      <td className="px-5 py-4 text-slate-600">{l.city || "-"}</td>
                      <td className="px-5 py-4 text-slate-600">{l.email}</td>
                      <td className="px-5 py-4 text-slate-500">{fmtDate(l.created_at)}</td>
                      <td className="px-5 py-4">
                        <Select value={l.status} onValueChange={(v) => updateStatus(l.id, v)}>
                          <SelectTrigger className={`h-8 w-[140px] rounded-full border-0 text-xs font-semibold ${statusColor[l.status] || ""}`} data-testid={`admin-status-${l.id}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-5 py-4">
                        <button onClick={() => setDetail(l)} className="text-sm font-semibold text-ocean hover:underline" data-testid={`admin-view-${l.id}`}>View</button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>

      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-h-[85vh] max-w-lg overflow-y-auto" data-testid="admin-detail-modal">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-navy">Lead Details</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-2 text-sm">
              {Object.entries(detail).filter(([k]) => !["type", "photos"].includes(k)).map(([k, v]) => (
                <div key={k} className="flex justify-between gap-4 border-b border-line py-2">
                  <span className="font-medium capitalize text-slate-500">{k.replace(/_/g, " ")}</span>
                  <span className="text-right text-navy">{Array.isArray(v) ? (v.join(", ") || "-") : String(v || "-")}</span>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Admin() {
  const { user, loading, logout } = useAuth();
  if (loading) {
    return <div className="flex min-h-screen items-center justify-center bg-navy"><Loader2 className="h-8 w-8 animate-spin text-sky" /></div>;
  }
  if (!user) return <LoginScreen />;
  if (!user.is_admin) return <Unauthorized onLogout={logout} />;
  return <Dashboard />;
}
